/**
 * @desc: Filter 筛选器组件 - 内部维护 draft 值，外部 value 为确认值
 */
import React, { useRef, useState } from 'react';
import { useIntl } from 'umi';
import { isEqual } from 'lodash-es';
import { useLatest, useDeepCompareEffect } from 'ahooks';
import { normalizeFilterValuesByItems } from '@intelligence/v3/filterControls/apiParams';
import FilterView from './components/FilterView';
import FilterSaveFeature, { IFilterSaveFeatureRenderProps } from './components/FilterSaveFeature';
import useGetState from '@intelligence/hooks/useGetState';
import { getChangedFields, mergeFilterSlotContents } from './utils';

import './registerBuiltInControls';

import type {
  IFilterDraftTransformContext,
  IFilterGroup,
  IFilterItemConfig,
  IFilterValues,
} from '@intelligence/v3/filterCore/types';

import './index.scss';

interface IFilterProps extends IBaseProps {
  /** 筛选器是否处于加载态；加载时禁用操作区并展示骨架屏 */
  loading?: boolean;
  /** 重置按钮的悬浮提示文案；为空时不展示提示内容 */
  resetTooltip?: string;
  /** My Filter 保存方案类型；同一类型下的方案共享列表 */
  saveFilterType?: string;
  /** 点击 Reset 时使用的基准值；未传时回退到当前 confirmed value */
  resetValue?: IFilterValues;
  /**
   * 外部加载的筛选值，只写入内部 draft。
   * 适用于 My Filter 这类“加载但不自动 Confirm”的场景。
   */
  loadedValue?: IFilterValues;
  /** loadedValue 的版本标识；值变化时即使 loadedValue 深相等也会重新应用 */
  loadedValueVersion?: string | number;
  /** 插入到筛选器项区域最前面的扩展内容；开启 useSaveFilter 时会排在 My Filter 后面 */
  beforeItemsContent?: React.ReactNode;
  /** 吸顶默认头部区域的前置静态内容；开启 useSaveFilter 时会排在 My Filter 静态标识后面 */
  stickyBeforeSummaryContent?: React.ReactNode;
  /** 暴露给外部页面的额外操作内容，渲染在操作按钮之后 */
  extraActions?: React.ReactNode;
  /** 是否展示默认确认/重置操作区 */
  useActionBar?: boolean;
  /** 是否启用 My Filter 保存和加载方案能力 */
  useSaveFilter?: boolean;
  /** 是否启用自定义布局 class，默认使用标准 flex 布局 */
  useCustomLayout?: boolean;
  /** 是否启用更多筛选折叠逻辑 */
  useMoreFilter?: boolean;
  /** 是否启用吸顶摘要层 */
  useSticky?: boolean;
  /** 吸顶摘要层距离滚动容器顶部的偏移量 */
  stickyTopOffset?: number;
  /** 吸顶面板挂载容器，默认 document.body */
  stickyPortalContainer?: HTMLElement | string | (() => HTMLElement | null) | null;
  /** 吸顶摘要层标题，支持 dbi_ i18n key */
  stickyHeaderTitle?: string;
  /** 筛选器分组配置；未传时所有可见项渲染在默认分组，传入后未分组的 visible item 仍保留在头部分组 */
  filterGroups?: IFilterGroup[];
  /** 变化时清空 My Filter 预设选中 */
  presetClearToken?: number;
  /** 筛选器配置项，决定渲染控件、值归一化和 API/URL 字段映射 */
  items: IFilterItemConfig[];
  /**
   * 外部 confirmed value。
   * Filter 内部会基于它初始化 draft，并在外部确认值变化时同步 draft。
   */
  value: IFilterValues;
  /** draft 变化、重置或加载方案后执行的页面级值修正函数 */
  transformDraftValues?: (values: IFilterValues, context: IFilterDraftTransformContext) => IFilterValues;
  /** draft 字段变更回调，Confirm 前也会触发 */
  onChange?: (field: string, fieldValue: unknown, allValues: IFilterValues) => void;
  /** 用户点击 Confirm 后返回当前 draft，页面通常在这里更新 confirmed value */
  onConfirm?: (values: IFilterValues) => void;
  /** 用户点击 Reset 后返回重置后的 draft，不会自动触发 Confirm */
  onReset?: (values: IFilterValues) => void;
  /** 更多筛选展开状态变化回调 */
  onToggleExpand?: (expanded: boolean) => void;
  /** loadedValue 实际写入 draft 后触发，返回变更字段和加载前基准值 */
  onFilterLoaded?: (values: IFilterValues, meta: { changedFields: string[]; baseValues: IFilterValues }) => void;
}

/**
 * Filter 组件
 *
 * 核心职责：
 * 1. 接收外部 value prop（已确认的值）作为初始值
 * 2. 内部维护 draftValue state 保存用户交互中未确认的值
 * 3. Confirm 后将 draftValue 通过 onConfirm 回调传出
 * 4. Reset 后将 draftValue 重置为 resetValue（若未传则回 confirmed value）
 *
 * 不做的事：
 * - 不做值转换（pageParams / urlParams 由上层处理）
 * - 不处理业务日志/埋点（上层在 onChange 中自行处理）
 * - 不管理 URL / sessionStorage（上层处理）
 */
const Filter: React.FC<IFilterProps> = (props) => {
  const {
    style,
    className,
    loading = false,
    items,
    value,
    resetValue,
    loadedValue,
    loadedValueVersion,
    beforeItemsContent,
    stickyBeforeSummaryContent,
    transformDraftValues,
    onChange,
    onConfirm,
    onReset,
    onFilterLoaded,
    useSticky = false,
    stickyTopOffset = 0,
    stickyPortalContainer,
    stickyHeaderTitle,
    useSaveFilter = false,
    saveFilterType,
    presetClearToken,
    ...restProps
  } = props;

  const { locale } = useIntl();

  const onChangeRef = useLatest(onChange);

  /**
   * 按当前 items 对传入值做字段裁剪和控件级归一化。
   * Filter 内部所有 confirmed、draft、loadedValue 都先走这一步。
   */
  const getNormalizedValues = (values?: IFilterValues): IFilterValues => {
    return normalizeFilterValuesByItems(items, values);
  };

  /**
   * 执行页面传入的 draft 修正逻辑。
   * 只处理值形态，不触发 Confirm 或外部同步。
   */
  const applyDraftTransform = (
    nextValues: IFilterValues,
    context: Parameters<NonNullable<IFilterProps['transformDraftValues']>>[1],
  ): IFilterValues => {
    if (!transformDraftValues) {
      return nextValues;
    }

    return transformDraftValues(nextValues, context);
  };

  // 内部 draft 值，用户交互时实时更新，Confirm 前不对外生效
  const [draftValue, setDraftValue, getDraftValue] = useGetState<IFilterValues>(() => getNormalizedValues(value));
  const latestConfirmedValuesRef = useRef<IFilterValues>(getNormalizedValues(value));
  const latestLoadedValuesRef = useRef<IFilterValues | undefined>(undefined);
  const latestLoadedValueVersionRef = useRef<string | number | undefined>(undefined);
  const isPristineRef = useRef(true);
  const submittedDraftValuesRef = useRef<IFilterValues | null>(null);

  /**
   * 判断 draft 是否与 confirmed value 一致（未被修改）。
   */
  const getPristineState = (
    nextDraftValues: IFilterValues,
    confirmedValues = latestConfirmedValuesRef.current,
  ): boolean => {
    return isEqual(nextDraftValues, confirmedValues);
  };

  // pristine：draft 与 confirmed value 一致（未被修改）时为 true
  // 使用 bool state 代替 JSON.stringify 深比较，避免 key 顺序不稳定和序列化开销
  const [pristine, setPristine] = useState(true);

  /** 同步 pristine ref 和 state，供事件和 effect 读取同一状态 */
  const syncPristineState = (nextPristine: boolean): void => {
    isPristineRef.current = nextPristine;
    setPristine(nextPristine);
  };

  // 稳定的 handleChange 引用，避免 FilterView 中 renderedItems useMemo 因 onChange 引用变化而频繁失效
  /**
   * 处理控件字段变化。
   * 这里只更新内部 draft，并通过 onChange 暴露给页面，不修改 confirmed value。
   */
  const handleChange = (field: string, fieldValue: any): void => {
    const currentDraftValues = getDraftValue();
    const confirmedValues = latestConfirmedValuesRef.current;
    const nextValues = applyDraftTransform(
      { ...currentDraftValues, [field]: fieldValue },
      {
        reason: 'change',
        field,
        fieldValue,
        currentDraftValues,
        confirmedValues,
        items,
      },
    );

    if (isEqual(currentDraftValues, nextValues)) {
      return;
    }

    submittedDraftValuesRef.current = null;
    setDraftValue(nextValues);
    syncPristineState(getPristineState(nextValues, confirmedValues));
    onChangeRef.current?.(field, nextValues[field] as any, nextValues);
  };

  /**
   * Confirm 时把当前 draft 交给页面。
   * 是否更新 confirmed value 和同步 URL/短链由页面决定。
   */
  const handleConfirm = (): void => {
    const draftValueNew = getDraftValue();
    submittedDraftValuesRef.current = draftValueNew;
    onConfirm?.(draftValueNew);
  };

  /**
   * Reset 时回到 resetValue 或当前 confirmed value。
   * Reset 后仍停留在 draft，不自动触发 Confirm。
   */
  const handleReset = (): void => {
    const currentDraftValues = getDraftValue();
    const confirmedValues = latestConfirmedValuesRef.current;
    const resetBaseValues = getNormalizedValues(resetValue ?? value ?? {});
    const nextValues = applyDraftTransform(resetBaseValues, {
      reason: 'reset',
      currentDraftValues,
      confirmedValues,
      items,
    });

    submittedDraftValuesRef.current = null;
    setDraftValue(nextValues);
    syncPristineState(getPristineState(nextValues, confirmedValues));
    onReset?.(nextValues);
  };

  /**
   * 应用外部加载值到 draft。
   * 适用于 My Filter，加载后不自动修改 confirmed value。
   */
  const applyLoadedValues = (values: IFilterValues): void => {
    const currentDraftValues = getDraftValue();
    const confirmedValues = latestConfirmedValuesRef.current;
    const baseValues = currentDraftValues;
    const normalizedLoadedValues = getNormalizedValues(values);
    const nextValues = applyDraftTransform(
      {
        ...baseValues,
        ...normalizedLoadedValues,
      },
      {
        reason: 'load',
        currentDraftValues,
        confirmedValues,
        items,
      },
    );
    const changedFields = getChangedFields(currentDraftValues, nextValues);

    if (changedFields.length === 0) {
      onFilterLoaded?.(nextValues, {
        changedFields,
        baseValues,
      });
      return;
    }

    submittedDraftValuesRef.current = null;
    setDraftValue(nextValues);
    syncPristineState(getPristineState(nextValues, confirmedValues));

    changedFields.forEach((field) => {
      onChangeRef.current?.(field, nextValues[field] as any, nextValues);
    });

    onFilterLoaded?.(nextValues, {
      changedFields,
      baseValues,
    });
  };

  // 外部 value 变化时同步 draft 并设为 pristine
  useDeepCompareEffect(() => {
    const nextConfirmedValues = getNormalizedValues(value);
    const prevConfirmedValues = latestConfirmedValuesRef.current;
    const currentDraftValues = getDraftValue();
    const submittedDraftValues = submittedDraftValuesRef.current;

    if (isEqual(prevConfirmedValues, nextConfirmedValues)) {
      return;
    }

    latestConfirmedValuesRef.current = nextConfirmedValues;

    if (
      isPristineRef.current ||
      isEqual(currentDraftValues, nextConfirmedValues) ||
      isEqual(currentDraftValues, prevConfirmedValues) ||
      (submittedDraftValues !== null && isEqual(currentDraftValues, submittedDraftValues))
    ) {
      submittedDraftValuesRef.current = null;
      setDraftValue(nextConfirmedValues);
      syncPristineState(true);
      return;
    }

    syncPristineState(getPristineState(currentDraftValues, nextConfirmedValues));
  }, [value, items]);

  useDeepCompareEffect(() => {
    if (loadedValue === undefined) {
      latestLoadedValuesRef.current = undefined;
      latestLoadedValueVersionRef.current = undefined;
      return;
    }

    const nextLoadedValues = getNormalizedValues(loadedValue);
    const hasLoadedValuesChanged = !isEqual(latestLoadedValuesRef.current, nextLoadedValues);
    const hasLoadedValueVersionChanged = latestLoadedValueVersionRef.current !== loadedValueVersion;

    if (!hasLoadedValuesChanged && !hasLoadedValueVersionChanged) {
      return;
    }

    latestLoadedValuesRef.current = nextLoadedValues;
    latestLoadedValueVersionRef.current = loadedValueVersion;
    applyLoadedValues(nextLoadedValues);
  }, [loadedValue, loadedValueVersion, items]);

  /**
   * 组装 FilterView 插槽。
   * My Filter、页面自定义内容按固定顺序注入，避免各能力互相覆盖。
   */
  const renderFilterView = (featureProps?: IFilterSaveFeatureRenderProps): React.ReactElement => {
    const resolvedBeforeItemsContent = mergeFilterSlotContents(featureProps?.beforeItemsContent, beforeItemsContent);
    const resolvedStickyBeforeSummaryContent = mergeFilterSlotContents(
      featureProps?.stickyBeforeSummaryContent,
      stickyBeforeSummaryContent,
    );

    return (
      <FilterView
        {...restProps}
        style={style}
        className={className}
        locale={locale}
        loading={loading}
        pristine={pristine}
        items={items}
        value={draftValue}
        useSticky={useSticky}
        stickyTopOffset={stickyTopOffset}
        stickyPortalContainer={stickyPortalContainer}
        stickyHeaderTitle={stickyHeaderTitle}
        showSavedFilterIndicator={useSaveFilter}
        beforeItemsContent={resolvedBeforeItemsContent}
        stickyBeforeSummaryContent={resolvedStickyBeforeSummaryContent}
        innerExtraActions={featureProps?.innerExtraActions}
        onChange={(field, fieldValue) => {
          featureProps?.handleDraftFieldChange(field, fieldValue);
          handleChange(field, fieldValue);
        }}
        onConfirm={handleConfirm}
        onReset={() => {
          featureProps?.handleReset();
          handleReset();
        }}
      />
    );
  };

  if (useSaveFilter) {
    return (
      <FilterSaveFeature
        loading={loading}
        filterSaveType={saveFilterType}
        items={items}
        currentValue={draftValue}
        onLoad={applyLoadedValues}
        presetClearToken={presetClearToken}
      >
        {(featureProps) => renderFilterView(featureProps)}
      </FilterSaveFeature>
    );
  }

  return renderFilterView();
};

export default Filter;
